import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, ArrowUpRight } from "lucide-react";

const STATUS_LABELS = {
  invited: "Invited",
  in_progress: "In progress",
  completed: "Completed",
};

const statusClass = (s) =>
  ({
    completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
    in_progress: "border-amber-200 bg-amber-50 text-amber-700",
    invited: "border-blue-200 bg-blue-50 text-blue-700",
  }[s] || "border-slate-200 bg-slate-50 text-slate-600");

const scoreClass = (s) => {
  if (s == null) return "text-slate-400";
  if (s >= 80) return "text-emerald-700";
  if (s >= 60) return "text-blue-700";
  if (s >= 40) return "text-amber-700";
  return "text-slate-500";
};

export default function CandidatesPanel({ open, onOpenChange, status }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const url = status
      ? `/dashboard/candidates?status=${encodeURIComponent(status)}`
      : "/dashboard/candidates";
    api
      .get(url)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, status]);

  const titleSuffix =
    status === "completed"
      ? "(completed)"
      : status === "in_progress"
      ? "(in progress)"
      : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="candidates-panel"
        className="max-h-[85vh] max-w-4xl overflow-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            All candidates {titleSuffix}
          </DialogTitle>
          <DialogDescription>
            {loading
              ? "Loading…"
              : `${items.length} candidate${items.length === 1 ? "" : "s"} across all your roles.`}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="candidates-panel-empty"
            className="py-12 text-center text-sm text-slate-500"
          >
            No candidates match this filter yet.
          </div>
        ) : (
          <div className="border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Candidate
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Role
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Status
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Score
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Started
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((iv) => (
                  <TableRow
                    key={iv.id}
                    data-testid={`candidates-row-${iv.id}`}
                    className="hover:bg-slate-50"
                  >
                    <TableCell>
                      <div className="font-medium">{iv.candidate_name}</div>
                      <div className="text-xs text-slate-500">
                        {iv.candidate_email}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{iv.job_title}</TableCell>
                    <TableCell>
                      <span
                        className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] ${statusClass(
                          iv.status
                        )}`}
                      >
                        {STATUS_LABELS[iv.status] || iv.status}
                      </span>
                    </TableCell>
                    <TableCell
                      className={`text-sm font-semibold ${scoreClass(
                        iv.overall_score
                      )}`}
                    >
                      {iv.overall_score != null
                        ? `${iv.overall_score}/10`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {iv.created_at
                        ? new Date(iv.created_at).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        data-testid={`candidates-open-${iv.id}`}
                        onClick={() => {
                          onOpenChange(false);
                          navigate(
                            `/jobs/${iv.job_id}/candidates/${iv.id}`
                          );
                        }}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-blue-700 hover:underline"
                      >
                        Open
                        <ArrowUpRight className="h-3 w-3" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
